import type { Status } from "@/shared/types/common.types";

export const ENDPOINT_KEYS = ["OCA", "OCD", "OCR", "OIE", "OMS"] as const;
export type EndpointKey = (typeof ENDPOINT_KEYS)[number];

export type EndpointHealthStatus = "Healthy" | "Unhealthy" | "NotDeployed" | "Unknown";
export type CustomerHealthCategory = "Healthy" | "Unhealthy" | "Incident" | "NoStatus" | "NotDeployed";
export type HealthFilter = "All" | CustomerHealthCategory;

export type EndpointMap<T> = {
  [K in EndpointKey]: T;
};

type ApiEndpointFields = {
  [K in EndpointKey as `${K}_LastChecked`]: string | null;
} & {
  [K in EndpointKey as `${K}_Status`]: EndpointHealthStatus | null;
} & {
  [K in EndpointKey as `${K}_StatusCode`]: number | null;
} & {
  [K in EndpointKey as `${K}_URL`]: string | null;
};

export interface EndpointInfo {
  lastChecked: string | null;
  status: EndpointHealthStatus;
  statusCode: number | null;
  url: string | null;
}

export interface ApiCustomerEnvironment extends ApiEndpointFields {
  "odata.etag"?: string;
  PartitionKey: string;
  RowKey: string;
  Timestamp: string;
  CustomerName: string;
  EnvironmentType: string;
  Product: string;
  UseCase: string | null;
}

export interface CustomerEnvironment {
  rowId?: string;
  customerName: string;
  environmentType: string;
  product: string;
  useCase: string;
  endpoints: EndpointMap<EndpointInfo>;
}

export type EndpointCell = EndpointInfo;

export interface CustomerRowBase {
  rowId: string;
  customerName: string;
  environmentType: string;
  useCase: string;
  category: CustomerHealthCategory;
}

export type CustomerRow = CustomerRowBase & EndpointMap<EndpointCell>;

export interface ServiceHealthSummary {
  allHealthy: number;
  unhealthy: number;
  incident: number;
  noStatus: number;
  endpointsUp: number;
  endpointsTotal: number;
  fleetAvailability: number;
  totalCustomers: number;
}

export interface ServiceHealthFilters {
  healthFilter: HealthFilter;
  search: string;
  selectedUseCases: string[];
  selectedCustomers: string[];
}

export interface ServiceHealthState {
  customerEnvironments: CustomerEnvironment[];
  lastRefreshedTime: string | null;
  error: string | null;
  status: {
    customerEnvironments: Status;
  };
  filters: ServiceHealthFilters;
  filtersInitialised: boolean;
}
